import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProductService } from '../../product.service';
import { ProductPackagingService } from '../../product-packaging.service';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { formulationBadgeClass, formulationLabel, Product, PackagingType, ProductPackaging } from '../../product.model';
import { MeasurementUnit } from '../../../raw-material-inventory/raw-material.model';
import { TabItem } from '../../../common/models/common.model';

@Component({
  selector: 'app-product-details',
  standalone: false,
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.scss',
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('addPackagingModal') addPackagingModalRef!: TemplateRef<any>;
  @ViewChild('manageModal') manageModalRef!: TemplateRef<any>;

  productId!: string;
  product!: Product;
  isProductDetailsLoading = true;
  isSavingPackaging = false;
  isEditing = false;

  // Add packaging form
  packagingForm!: FormGroup;
  units: MeasurementUnit[] = [];
  packagingTypes: PackagingType[] = [];
  imagePreview: string | ArrayBuffer | null = null;
  selectedImageFile: File | null = null;
  private modalRef!: NgbModalRef;
  private routeSub!: Subscription;

  protected readonly formulationLabel = formulationLabel;
  protected readonly formulationBadgeClass = formulationBadgeClass;

  activeTab: 'overview' | 'packaging' = 'overview';
  tabs: TabItem[] = [
    { label: 'Overview', value: 'overview', icon: 'bi bi-info-circle' },
    { label: 'Packaging', value: 'packaging', icon: 'bi bi-box-seam', badge: 0 },
  ];
  manageModalTab: 'units' | 'packaging-types' = 'units';

  // Manage units
  newUnitName = '';
  newUnitShortName = '';
  isCreatingUnit = false;
  deletingUnitId: string | null = null;

  // Manage packaging types
  newPackagingTypeName = '';
  isCreatingPackagingType = false;
  deletingPackagingTypeId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private productService: ProductService,
    private productPackagingService: ProductPackagingService,
    private rawMaterialService: RawMaterialService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.productId = params.get('id')!;
      this.getProductDetails();
    });
  }

  private getProductDetails(): void {
    this.isProductDetailsLoading = true;

    forkJoin([
      this.productService.get(this.productId),
      this.rawMaterialService.get_units(),
      this.productService.packaging_types(),
    ])
      .pipe(finalize(() => (this.isProductDetailsLoading = false)))
      .subscribe({
        next: ([product, units, packagingTypes]) => {
          this.product = product;
          this.units = units;
          this.packagingTypes = packagingTypes;
          this.updateTabBadges();
        },
        error: err => {
          console.error(err);
          this.router.navigate(['/products']);
        },
      });
  }

  private updateTabBadges(): void {
    const packagingTab = this.tabs.find(t => t.value === 'packaging');
    if (packagingTab) {
      packagingTab.badge = this.product.product_packaging.length;
    }
  }

  selectTab(tabValue: string): void {
    this.activeTab = tabValue as 'overview' | 'packaging';
  }

  onEditPackaging(packaging: ProductPackaging): void {
    this.router.navigate(['/products', this.productId, 'packaging', packaging.id]);
  }

  onAddPackaging(): void {
    this.activeTab = 'packaging';
    this.initPackagingForm();
    this.imagePreview = null;
    this.selectedImageFile = null;

    const modalOptions: NgbModalOptions = { backdrop: 'static', size: 'lg' };
    this.modalRef = this.modalService.open(this.addPackagingModalRef, modalOptions);
  }

  private initPackagingForm(): void {
    this.packagingForm = this.fb.group({
      packing_size: ['', [Validators.required]],
      packaging_type: [null],
      unit: ['', Validators.required],
      nos: ['', Validators.required],
      dealer_rate: ['', Validators.required],
      gst_rate: ['', Validators.required],
      mrp: ['', Validators.required],
      dealer_rate_incl_gst: [{ value: '0.00', disabled: true }],
    });

    // Set up auto-calculation for dealer rate including GST
    this.packagingForm.get('dealer_rate')?.valueChanges.subscribe(() => this.updateDealerRateInclGst());
    this.packagingForm.get('gst_rate')?.valueChanges.subscribe(() => this.updateDealerRateInclGst());
  }

  private updateDealerRateInclGst(): void {
    const dealerRate = parseFloat(this.packagingForm.get('dealer_rate')?.value || '0');
    const gstRate = parseFloat(this.packagingForm.get('gst_rate')?.value || '0');

    if (isNaN(dealerRate) || isNaN(gstRate)) {
      this.packagingForm.get('dealer_rate_incl_gst')?.setValue('0.00');
      return;
    }

    const result = dealerRate + (dealerRate * gstRate) / 100;
    this.packagingForm.get('dealer_rate_incl_gst')?.setValue(result.toFixed(2));
  }

  onImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedImageFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onRemoveImage(): void {
    this.imagePreview = null;
    this.selectedImageFile = null;

    const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
    if (fileInput) fileInput.value = '';
  }

  isInvalid(controlName: string): boolean {
    const control = this.packagingForm.get(controlName);
    return (control?.invalid && (control?.touched || control?.dirty)) ?? false;
  }

  onSubmitPackaging(): void {
    if (this.packagingForm.invalid) {
      this.packagingForm.markAllAsTouched();
      return;
    }

    this.isSavingPackaging = true;

    // Handle image upload with FormData if image is selected
    if (this.selectedImageFile) {
      const formData = new FormData();

      // Append all form fields to FormData
      formData.append('product', this.productId);
      formData.append('packing_size', this.packagingForm.value.packing_size);

      // Only append packaging_type if it has a value
      if (this.packagingForm.value.packaging_type) {
        formData.append('packaging_type', this.packagingForm.value.packaging_type);
      }

      formData.append('unit', this.packagingForm.value.unit);
      formData.append('nos', this.packagingForm.value.nos);
      formData.append('dealer_rate', this.packagingForm.value.dealer_rate);
      formData.append('gst_rate', this.packagingForm.value.gst_rate);
      formData.append('mrp', this.packagingForm.value.mrp);
      formData.append('image_url', this.selectedImageFile);

      this.productPackagingService
        .postFormData(formData)
        .pipe(finalize(() => (this.isSavingPackaging = false)))
        .subscribe({
          next: newPackaging => {
            console.log('Packaging added successfully', newPackaging);
            this.modalRef?.dismiss();
            this.getProductDetails();
          },
          error: err => {
            console.error('Error adding packaging:', err);
          },
        });
    } else {
      // No image - use regular JSON payload
      const payload = {
        product: this.productId,
        ...this.packagingForm.value,
        bill_of_material: { materials: [] },
      };

      this.productPackagingService
        .post(payload)
        .pipe(finalize(() => (this.isSavingPackaging = false)))
        .subscribe({
          next: newPackaging => {
            console.log('Packaging added successfully', newPackaging);
            this.modalRef?.dismiss();
            this.getProductDetails();
          },
          error: err => {
            console.error('Error adding packaging:', err);
          },
        });
    }
  }

  onDeletePackaging(packaging: ProductPackaging): void {
    const size = `${packaging.packing_size} ${packaging.unit?.short_name ?? ''}`.trim();
    const label = packaging.packaging_type ? `${size} ${packaging.packaging_type.name}` : size;
    const confirmDelete = confirm(
      `Are you sure you want to delete the packaging size ${label}?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    this.productPackagingService
      .delete(packaging.id)
      .pipe(finalize(() => {}))
      .subscribe({
        next: () => {
          console.log('Packaging deleted successfully');
          // Refresh product details to update the list
          this.getProductDetails();
        },
        error: err => {
          console.error('Error deleting packaging:', err);
          alert('Failed to delete packaging. Please try again.');
        },
      });
  }

  onManage(): void {
    this.manageModalTab = 'units';
    this.newUnitName = '';
    this.newUnitShortName = '';
    this.newPackagingTypeName = '';
    this.modalService.open(this.manageModalRef, { backdrop: 'static', size: 'md' });
  }

  onCreateUnit(): void {
    const name = this.newUnitName.trim();
    const short_name = this.newUnitShortName.trim();
    if (!name || !short_name) return;

    this.isCreatingUnit = true;
    this.rawMaterialService
      .post_unit(name, short_name)
      .pipe(finalize(() => (this.isCreatingUnit = false)))
      .subscribe({
        next: unit => {
          this.units = [...this.units, unit];
          this.newUnitName = '';
          this.newUnitShortName = '';
        },
      });
  }

  onDeleteUnit(id: string): void {
    this.deletingUnitId = id;
    this.rawMaterialService
      .delete_unit(id)
      .pipe(finalize(() => (this.deletingUnitId = null)))
      .subscribe({
        next: () => {
          this.units = this.units.filter(u => u.id !== id);
        },
      });
  }

  onCreatePackagingType(): void {
    const name = this.newPackagingTypeName.trim();
    if (!name) return;

    this.isCreatingPackagingType = true;
    this.productService
      .createPackagingType(name)
      .pipe(finalize(() => (this.isCreatingPackagingType = false)))
      .subscribe({
        next: pt => {
          this.packagingTypes = [...this.packagingTypes, pt];
          this.newPackagingTypeName = '';
        },
      });
  }

  onDeletePackagingType(id: string): void {
    this.deletingPackagingTypeId = id;
    this.productService
      .deletePackagingType(id)
      .pipe(finalize(() => (this.deletingPackagingTypeId = null)))
      .subscribe({
        next: () => {
          this.packagingTypes = this.packagingTypes.filter(pt => pt.id !== id);
        },
      });
  }

  onBackToProducts(): void {
    void this.router.navigate(['/products']);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }
}
